import { UnitLabel } from '@/shared/config/config'
import useI18n from '@/shared/hooks/useI18n'
import {
  Table,
  TableRow,
  TableColumnHeader,
  TableCell,
  generatePeriodId
} from '@/common'
import { useCallback, useState, useEffect } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { PrecursorMaterialWithPrecursorEmissionReport } from '@/hooks/api/v2/precursorMaterials'
import CnCodeTag from '@/components/CnCodeTag'
import useOrganizationDocuments from '@/shared/hooks/useOrganizationDocuments'
import { Link } from 'react-router-dom'
import { useReportFormatting } from '@/shared/hooks/useReportFormatting'

function AllocatedPrecursorTableRow({
  material,
  link
}: {
  material: PrecursorMaterialWithPrecursorEmissionReport
  link: string
}) {
  const { generateDownloadUrl } = useOrganizationDocuments()
  const { formatReportPeriod } = useReportFormatting()
  const [url, setUrl] = useState<string>('')

  useEffect(() => {
    const fetchUrl = async () => {
      if (material.proofFilePath) {
        const url = await generateDownloadUrl(material.proofFilePath)
        if (url) {
          setUrl(url)
        }
      }
    }
    if (!url) {
      fetchUrl()
    }
  }, [generateDownloadUrl, material.proofFilePath, url])

  return (
    <TableRow key={material.id}>
      <TableCell>
        <Link
          className="text-blue-500 hover:underline"
          to={`/suppliers?supplierId=${material.precursorEmissionReport?.precursorSupplier?.id}&precursorEmissionReportId=${material.precursorEmissionReport?.id}`}
        >
          {material.precursorEmissionReport?.goodsDescription}
        </Link>
      </TableCell>
      <TableCell>
        <CnCodeTag cnCode={material.precursorEmissionReport?.cnCode} />
      </TableCell>
      <TableCell>
        <Link
          className="text-blue-500 hover:underline"
          to={`/suppliers?supplierId=${material.precursorEmissionReport?.precursorSupplier?.id}`}
        >
          {material.precursorEmissionReport?.precursorSupplier?.name}
        </Link>
      </TableCell>
      <TableCell>{material.emissionReport?.productionRoute?.name}</TableCell>
      <TableCell>
        <NavLink className="text-blue-500 hover:underline" to={link}>
          {formatReportPeriod(
            new Date(material.emissionReport?.startDate ?? ''),
            new Date(material.emissionReport?.endDate ?? '')
          )}
        </NavLink>
      </TableCell>
      <TableCell>
        {material.proofFilePath ? (
          <a href={url} download={material.proofFilePath} className="text-blue-500 hover:underline">
            {material.proofFilePath}
          </a>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell className="text-right font-mono">
        {material.quantityInTonnes} {UnitLabel.t}
      </TableCell>
    </TableRow>
  )
}

export default function AllocatedPrecursorTable({
  materials
}: {
  materials: PrecursorMaterialWithPrecursorEmissionReport[]
}) {
  const { t } = useI18n()
  const installationId = useParams<{ installationId: string }>().installationId!

  const getReportLink = useCallback(
    (material: PrecursorMaterialWithPrecursorEmissionReport) => {
      if (!material?.emissionReport) {return ''}
      const periodId = generatePeriodId(
        new Date(material?.emissionReport?.startDate ?? ''),
        new Date(material?.emissionReport?.endDate ?? '')
      )
      return `/dashboard/${installationId}/production-routes/${material?.emissionReport?.productionRoute?.id}/reports/${periodId}/view/${material?.emissionReport?.id}`
    },
    [installationId]
  )

  return (
    <Table>
      <thead>
        <TableRow>
          <TableColumnHeader className="w-56">
            {t('materials:allocatedStockTable.headers.goodsDescription')}
          </TableColumnHeader>
          <TableColumnHeader className="w-56">
            {t('materials:allocatedStockTable.headers.cnCode')}
          </TableColumnHeader>
          <TableColumnHeader className="w-56">
            {t('materials:allocatedStockTable.headers.supplier')}
          </TableColumnHeader>
          <TableColumnHeader className="w-56">
            {t('materials:allocatedStockTable.headers.productionRoute')}
          </TableColumnHeader>
          <TableColumnHeader className="w-56">
            {t('materials:allocatedStockTable.headers.report')}
          </TableColumnHeader>
          <TableColumnHeader className="w-56">
            {t('materials:allocatedStockTable.headers.evidence')}
          </TableColumnHeader>
          <TableColumnHeader className="text-right">
            {t('materials:allocatedStockTable.headers.quantity')}
          </TableColumnHeader>
        </TableRow>
      </thead>
      <tbody>
        {materials.map(material => (
          <AllocatedPrecursorTableRow
            key={material.id}
            material={material}
            link={getReportLink(material)}
          />
        ))}
      </tbody>
    </Table>
  )
}
